import pillIcon from '@cannect/assets/svg/discovery-pill.svg'
import stethoscopeIcon from '@cannect/assets/svg/discovery-stethoscope.svg'
import asmaCard from '@cannect/assets/img/home/conditions-cards/asma.webp'
import diabetesCard from '@cannect/assets/img/home/conditions-cards/diabetes.webp'
import dorFibromialgiaCard from '@cannect/assets/img/home/conditions-cards/dor-fibromialgia.webp'
import perdaDePesoCard from '@cannect/assets/img/home/conditions-cards/perda-de-peso.webp'
import saudeCardiovascularCard from '@cannect/assets/img/home/conditions-cards/saude-cardiovascular.webp'
import saudeDaMulherCard from '@cannect/assets/img/home/conditions-cards/saude-da-mulher.webp'
import saudeMentalCard from '@cannect/assets/img/home/conditions-cards/saude-mental.webp'
import sonoInsoniaCard from '@cannect/assets/img/home/conditions-cards/sono-insonia.webp'
import { ProductFilters, buildFilterUrl } from '@cannect/constants/productFilters'
import cannabis from '@cannect/assets/img/home/categories/cannabis.webp'
import cogumelos from '@cannect/assets/img/home/categories/cogumelos.webp'
import dermocosmeticos from '@cannect/assets/img/home/categories/dermocosmeticos.webp'
import higiene from '@cannect/assets/img/home/categories/higiene.webp'
import mamaeBebe from '@cannect/assets/img/home/categories/mamae-bebe.webp'
import medicamentos from '@cannect/assets/img/home/categories/medicamentos.webp'
import suplementos from '@cannect/assets/img/home/categories/suplementos.webp'
import testeGenetico from '@cannect/assets/img/home/categories/teste-genetico.webp'

export type TCategoryCard = {
  title: string
  description: string
  path: string
  image: string
  /** Cor de fundo do card. Pastéis pontuais do design, sem token equivalente. */
  background: string
  /**
   * Enquadramento da ilustração (Figma node 4333:740). A imagem é sempre um quadrado de lado
   * `imageSize` dentro de uma caixa `boxWidth x 64` com `overflow-hidden`, deslocada por
   * `offsetX`/`offsetY` — é isso que dá o corte e a escala de cada produto.
   */
  boxWidth: number
  imageSize: number
  offsetX: number
  offsetY: number
}

export type TResolvedClassIds = {
  /** Classe "Alopático" — o card "Medicamentos". */
  allopathicId?: number
  /** Classe "Higiene e Beleza" — os cards "Higiene e beleza" e "Dermocosméticos". */
  hygieneBeautyId?: number
}

/**
 * As oito categorias de produto (Figma node 4333:740).
 *
 * Todas apontam para /produtos: o design pressupõe navegação por categoria, mas não existe rota de
 * categoria no app — o helper de filtro (`_buildFilterUrl`) só existe comentado. Mesmo tratamento
 * dado aos itens de "Produtos e serviços" no footer.
 *
 * É função, e não constante, porque três cards dependem de classes cujo id é gerado pelo banco de
 * cada ambiente ("Alopático" e "Higiene e Beleza" nascem da sincronização do catálogo): em
 * desenvolvimento valem 17 e 16, em produção 10 e 9. Fixar o número faz o filtro devolver lista
 * vazia no ambiente onde o id não bate — foi exatamente o bug do PR #2459, que resolveu os outros
 * pontos do app por nome e não alcançou esta seção. Os ids chegam resolvidos por
 * `useProductClassId`; um `undefined` (classes ainda carregando) apenas omite o filtro, levando à
 * listagem completa em vez de a uma lista vazia.
 *
 * Os demais cards seguem com valor fixo de propósito: `class_id=6` (Nutracêuticos) e `class_id=8`
 * (Teste Genético) não são criados pela sincronização e têm o mesmo id nos dois ambientes.
 */
export const buildCategoryCards = ({ allopathicId, hygieneBeautyId }: TResolvedClassIds): TCategoryCard[] => [
  {
    title: 'Medicamentos',
    description: 'Referência a genéricos e uso constante',
    path: buildFilterUrl({ ...ProductFilters.ALLOPATHIC_ALL, class_id: allopathicId }),
    image: medicamentos,
    background: '#dce8e0',
    boxWidth: 69,
    imageSize: 84,
    offsetX: -7.65,
    offsetY: -10.3
  },
  {
    title: 'Cannabis medicinal',
    description: 'Acesso com prescrição e orientação',
    path: '/produtos/1?limit=16&translated_name=',
    image: cannabis,
    background: '#e2ead2',
    boxWidth: 69,
    imageSize: 67,
    offsetX: 0.99,
    offsetY: -3.94
  },
  {
    title: 'Suplementos',
    description: 'Vitaminas e minerais',
    path: '/produtos/1?limit=16&class_id=6&translated_name=',
    image: suplementos,
    background: '#f6ebc6',
    boxWidth: 59,
    imageSize: 77,
    offsetX: -9.01,
    offsetY: -4.01
  },
  {
    title: 'Cogumelos funcionais',
    description: 'Foco, energia e imunidade',
    path: '/produtos/1?limit=16&category_slug=foco-e-performance&translated_name=',
    image: cogumelos,
    background: '#e6ddd1',
    boxWidth: 84,
    imageSize: 88,
    offsetX: -4,
    offsetY: -13
  },
  {
    title: 'Higiene e beleza',
    description: 'Para a rotina de todo dia',
    path: buildFilterUrl({ ...ProductFilters.HYGIENE_BEAUTY_ALL, class_id: hygieneBeautyId }),
    image: higiene,
    background: '#ddebe7',
    boxWidth: 100,
    imageSize: 76,
    offsetX: -0.19,
    offsetY: -6
  },
  {
    title: 'Dermocosméticos',
    description: 'Rotina para a pele',
    path: buildFilterUrl({ ...ProductFilters.HYGIENE_BEAUTY_ALL, class_id: hygieneBeautyId }),
    image: dermocosmeticos,
    background: '#f0dbcb',
    boxWidth: 83,
    imageSize: 66,
    offsetX: -1.31,
    offsetY: -1.41
  },
  {
    title: 'Mamãe e bebê',
    description: 'Do enxoval ao dia a dia',
    path: '/produtos/1?limit=16&shopify_collection=mamae-e-bebe&translated_name=',
    image: mamaeBebe,
    background: '#f1deda',
    boxWidth: 82,
    imageSize: 93,
    offsetX: -5.32,
    offsetY: -12.13
  },
  {
    title: 'Teste genético',
    description: 'Cuidar antes de precisar',
    path: '/produtos/1?limit=16&class_id=8&translated_name=',
    image: testeGenetico,
    background: '#dce8f2',
    boxWidth: 152,
    imageSize: 71,
    offsetX: -4.32,
    offsetY: -2
  }
]

/**
 * Condições de saúde da aba "Quero cuidar da minha condição".
 *
 * Estes rótulos vêm da seção "Tratamento em um único lugar", que esta revisão do protótipo removeu
 * da home — a aba assumiu o papel dela. O protótipo não define destino por condição, e no app só
 * duas das oito têm rota própria (`/tratamentos/dor-cronica` e `/tratamentos/saude-mental`).
 * Por isso todas levam para `/tratamentos`, a listagem que já existe, em vez de rotas inventadas.
 */
/**
 * Os oito cards da aba "Quero cuidar da minha condição" (Figma node 4544:14215).
 *
 * A revisão trocou as pills de texto por cards com a mesma anatomia dos de categoria: ilustração
 * de 64px de altura, título de 14px SemiBold e apoio de 12px Light sobre um pastel próprio.
 *
 * As ilustrações já vêm recortadas na janela visível declarada no arquivo, então `object-cover`
 * reproduz o enquadramento sem transformação — `boxWidth` é só a largura da caixa de cada uma.
 *
 * O destino reaproveita `buildConditionPath` do hero: as oito condições existem lá e levar a mesma
 * condição a lugares diferentes conforme o card clicado seria arbitrário.
 */
export const HEALTH_CONDITION_CARDS: {
  title: string
  description: string
  image: string
  background: string
  boxWidth: number
  filterBy: 'pathology' | 'expertise'
  filterNames: string[]
}[] = [
  {
    title: 'Dor e fibromialgia',
    description: 'Manejo da dor crônica',
    image: dorFibromialgiaCard,
    background: '#F0DBCB',
    boxWidth: 69,
    filterBy: 'pathology',
    filterNames: ['Dor Crônica', 'Fibromialgia']
  },
  {
    title: 'Sono e insônia',
    description: 'Dormir e descansar melhor',
    image: sonoInsoniaCard,
    background: '#E4DDF0',
    boxWidth: 69,
    filterBy: 'pathology',
    filterNames: ['Insônia', 'Distúrbios do sono/ Insônia']
  },
  {
    title: 'Saúde mental',
    description: 'Ansiedade, humor e equilíbrio',
    image: saudeMentalCard,
    background: '#F6EBC6',
    boxWidth: 85,
    filterBy: 'pathology',
    filterNames: ['Ansiedade', 'Saúde Mental']
  },
  {
    title: 'Perda de peso',
    description: 'Peso, energia e metabolismo',
    image: perdaDePesoCard,
    background: '#F0DBCB',
    boxWidth: 84,
    filterBy: 'pathology',
    filterNames: ['Obesidade', 'Síndrome metabólica']
  },
  {
    title: 'Saúde cardiovascular',
    description: 'Coração e circulação',
    image: saudeCardiovascularCard,
    background: '#DCE8E0',
    boxWidth: 100,
    filterBy: 'pathology',
    filterNames: ['Hipertensão e doenças hipertensivas', 'Doença cardíaca']
  },
  {
    title: 'Saúde da mulher',
    description: 'Cuidado em cada fase',
    image: saudeDaMulherCard,
    background: '#F1DEDA',
    boxWidth: 83,
    filterBy: 'expertise',
    filterNames: ['Ginecologia e obstetrícia', 'Ginecologia']
  },
  {
    title: 'Diabetes',
    description: 'Controle da glicemia no dia a dia',
    image: diabetesCard,
    background: '#EFE7D6',
    boxWidth: 109,
    filterBy: 'pathology',
    filterNames: ['Diabetes Mellitus']
  },
  {
    title: 'Asma',
    description: 'Respiração, controle e bem-estar',
    image: asmaCard,
    background: '#E2EAD2',
    boxWidth: 152,
    filterBy: 'pathology',
    filterNames: ['Asma', 'Doença de obstrução pulmonar crônica']
  }
]

export const CONDITIONS_PATH = '/tratamentos'

/**
 * As duas abas (node 4439:13628). A revisão de 2026-07-31 devolveu os ícones e encurtou os rótulos.
 *
 * O rótulo da segunda virou "Encontre seu médico", mas o arquivo não desenhou conteúdo para ela — o
 * nó com os cards de condição (4544:14215) foi apagado e nada entrou no lugar. Por decisão do time,
 * o painel segue com os cards de condição sob o rótulo novo, ciente de que rótulo e conteúdo não
 * conversam; revisitar quando existir uma busca de prescritor (hoje só há a listagem em
 * `/profissionais-parceiros-cannabis-medicinal`).
 */
export const DISCOVERY_TABS = [
  { value: 'products', label: 'Encontre seus medicamentos', icon: pillIcon },
  { value: 'conditions', label: 'Encontre seu médico', icon: stethoscopeIcon }
]
